<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white shadow rounded-lg mb-6">
        <div class="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Generar Factura</h1>
            <p class="text-sm text-gray-500">Orden #{{ ordenId }}</p>
          </div>
          <button @click="$router.push({ name: 'cliente-ordenes' })" class="text-indigo-600 hover:text-indigo-500 flex items-center text-sm">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Volver a órdenes
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loadingInit" class="text-center py-12">
        <svg class="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="text-gray-600">Cargando datos...</p>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Datos Fiscales -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Datos Fiscales</h2>
            <p v-if="datosFiscalesExistentes" class="text-xs text-green-600 mt-1">Datos cargados de tu perfil</p>
            <p v-else class="text-xs text-gray-500 mt-1">Completa tus datos fiscales</p>
          </div>
          <form @submit.prevent="onSubmit" class="px-6 py-4 space-y-4">
            <!-- RFC -->
            <div>
              <label for="rfc" class="block text-sm font-medium text-gray-700">RFC *</label>
              <input id="rfc" type="text" v-model="form.rfc" required maxlength="13" placeholder="XAXX010101000"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.rfc }" />
              <p v-if="errores.rfc" class="mt-1 text-sm text-red-600">{{ errores.rfc }}</p>
            </div>
            <!-- Razón Social -->
            <div>
              <label for="razon_social" class="block text-sm font-medium text-gray-700">Razón Social *</label>
              <input id="razon_social" type="text" v-model="form.razon_social" required maxlength="255"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.razon_social }" />
              <p v-if="errores.razon_social" class="mt-1 text-sm text-red-600">{{ errores.razon_social }}</p>
            </div>
            <!-- Código Postal -->
            <div>
              <label for="cp" class="block text-sm font-medium text-gray-700">Código Postal Fiscal *</label>
              <input id="cp" type="text" v-model="form.codigo_postal_fiscal" required maxlength="5" pattern="[0-9]{5}" placeholder="44100"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.codigo_postal_fiscal }" />
              <p v-if="errores.codigo_postal_fiscal" class="mt-1 text-sm text-red-600">{{ errores.codigo_postal_fiscal }}</p>
            </div>
            <!-- Régimen Fiscal -->
            <div>
              <label for="regimen" class="block text-sm font-medium text-gray-700">Régimen Fiscal *</label>
              <select id="regimen" v-model="form.regimen_fiscal" required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                <option value="">Selecciona</option>
                <option v-for="r in catalogos.regimenes" :key="r.clave" :value="r.clave">{{ r.clave }} - {{ r.descripcion }}</option>
              </select>
              <p v-if="errores.regimen_fiscal" class="mt-1 text-sm text-red-600">{{ errores.regimen_fiscal }}</p>
            </div>
            <!-- Uso CFDI -->
            <div>
              <label for="uso" class="block text-sm font-medium text-gray-700">Uso de CFDI *</label>
              <select id="uso" v-model="form.uso_cfdi" required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                <option value="">Selecciona</option>
                <option v-for="u in catalogos.usosCfdi" :key="u.clave" :value="u.clave">{{ u.clave }} - {{ u.descripcion }}</option>
              </select>
              <p v-if="errores.uso_cfdi" class="mt-1 text-sm text-red-600">{{ errores.uso_cfdi }}</p>
            </div>
            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email de Facturación *</label>
              <input id="email" type="email" v-model="form.email_facturacion" required maxlength="255"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': errores.email_facturacion }" />
              <p v-if="errores.email_facturacion" class="mt-1 text-sm text-red-600">{{ errores.email_facturacion }}</p>
            </div>
            <!-- Forma de Pago -->
            <div>
              <label for="forma_pago" class="block text-sm font-medium text-gray-700">Forma de Pago *</label>
              <select id="forma_pago" v-model="formaPago" required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                <option value="">Selecciona</option>
                <option v-for="f in catalogos.formasPago" :key="f.clave" :value="f.clave">{{ f.clave }} - {{ f.descripcion }}</option>
              </select>
            </div>

            <!-- Guardar datos + Generar -->
            <div class="pt-4 space-y-2">
              <button type="button" @click="guardarDatos" :disabled="guardando"
                class="w-full py-2 px-4 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-md hover:bg-indigo-50 disabled:opacity-50">
                {{ guardando ? 'Guardando...' : 'Guardar Datos Fiscales' }}
              </button>
              <button type="submit" :disabled="generando || !formaPago"
                class="w-full py-2 px-4 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {{ generando ? 'Generando Factura...' : 'Generar Factura' }}
              </button>
            </div>

            <div v-if="errorGeneral" class="p-3 bg-red-50 rounded-md">
              <p class="text-sm text-red-600">{{ errorGeneral }}</p>
            </div>
            <div v-if="msgExito" class="p-3 bg-green-50 rounded-md">
              <p class="text-sm text-green-600">{{ msgExito }}</p>
            </div>
          </form>
        </div>

        <!-- Panel derecho: resultado factura -->
        <div v-if="facturaGenerada" class="bg-white shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-center">
              <div class="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
              </div>
              <h2 class="ml-3 text-lg font-medium text-gray-900">Factura Generada</h2>
            </div>
          </div>
          <div class="px-6 py-4 space-y-3">
            <div class="bg-gray-50 rounded-md p-4 space-y-2">
              <p class="text-sm"><span class="font-medium">Folio:</span> {{ facturaGenerada.serie }}-{{ facturaGenerada.folio }}</p>
              <p class="text-sm"><span class="font-medium">UUID:</span> {{ facturaGenerada.uuid }}</p>
              <p class="text-sm"><span class="font-medium">Total:</span> ${{ formatMonto(facturaGenerada.total) }}</p>
              <p class="text-sm"><span class="font-medium">Fecha:</span> {{ formatFecha(facturaGenerada.fecha) }}</p>
            </div>
            <div class="space-y-2">
              <button @click="descargarPDF" :disabled="descargando.pdf"
                class="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                {{ descargando.pdf ? 'Descargando...' : 'Descargar PDF' }}
              </button>
              <button @click="descargarXML" :disabled="descargando.xml"
                class="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                {{ descargando.xml ? 'Descargando...' : 'Descargar XML' }}
              </button>
            </div>
            <p v-if="errorDescarga" class="text-sm text-red-600">{{ errorDescarga }}</p>
            <router-link :to="{ name: 'cliente-facturas' }" class="block text-center text-sm text-indigo-600 hover:text-indigo-500 mt-4">
              Ver todas mis facturas →
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import facturacionCliente from '@/Services/facturacionCliente'
import { useClienteAuthStore } from '@/stores/clienteAuth'

export default {
  name: 'GenerarFactura',
  data() {
    return {
      ordenId: this.$route.params.ordenId,
      loadingInit: true,
      guardando: false,
      generando: false,
      datosFiscalesExistentes: false,
      catalogos: { regimenes: [], usosCfdi: [], formasPago: [] },
      form: {
        rfc: '',
        razon_social: '',
        codigo_postal_fiscal: '',
        regimen_fiscal: '',
        uso_cfdi: '',
        email_facturacion: ''
      },
      formaPago: '',
      errores: {},
      errorGeneral: '',
      msgExito: '',
      facturaGenerada: null,
      descargando: { pdf: false, xml: false },
      errorDescarga: ''
    }
  },
  mounted() {
    const store = useClienteAuthStore()
    if (!store.isAuthenticated) {
      this.$router.push({ name: 'cliente-login' })
      return
    }
    this.init()
  },
  methods: {
    async init() {
      this.loadingInit = true
      try {
        // Cargar catálogos y datos fiscales en paralelo
        const [catRes, dfRes] = await Promise.all([
          facturacionCliente.getCatalogos(),
          facturacionCliente.getDatosFiscales()
        ])
        if (catRes.success) {
          this.catalogos = catRes.data
        }
        if (dfRes.success && dfRes.data) {
          this.datosFiscalesExistentes = true
          this.form.rfc = dfRes.data.rfc || ''
          this.form.razon_social = dfRes.data.razon_social || ''
          this.form.codigo_postal_fiscal = dfRes.data.codigo_postal_fiscal || ''
          this.form.regimen_fiscal = dfRes.data.regimen_fiscal || ''
          this.form.uso_cfdi = dfRes.data.uso_cfdi || ''
          this.form.email_facturacion = dfRes.data.email_facturacion || ''
        }
        // Si alguno retorna 401, redirigir
        if (catRes.status === 401 || dfRes.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
        }
      } catch (e) {
        this.errorGeneral = 'Error cargando datos iniciales.'
      } finally {
        this.loadingInit = false
      }
    },
    async guardarDatos() {
      this.guardando = true
      this.errores = {}
      this.errorGeneral = ''
      this.msgExito = ''
      const res = await facturacionCliente.guardarDatosFiscales(this.form)
      if (res.success) {
        this.msgExito = 'Datos fiscales guardados correctamente.'
        this.datosFiscalesExistentes = true
      } else {
        if (res.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
          return
        }
        if (res.errors) {
          this.errores = res.errors
        } else {
          this.errorGeneral = res.message
        }
      }
      this.guardando = false
    },
    async onSubmit() {
      this.generando = true
      this.errores = {}
      this.errorGeneral = ''
      this.msgExito = ''

      // Primero guardar datos fiscales
      const saveRes = await facturacionCliente.guardarDatosFiscales(this.form)
      if (!saveRes.success) {
        if (saveRes.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
          return
        }
        if (saveRes.errors) {
          this.errores = saveRes.errors
        } else {
          this.errorGeneral = saveRes.message
        }
        this.generando = false
        return
      }

      // Generar factura
      const res = await facturacionCliente.generarFactura(parseInt(this.ordenId), this.formaPago)
      if (res.success) {
        this.facturaGenerada = res.data
      } else {
        if (res.status === 401) {
          const store = useClienteAuthStore()
          store.logout()
          this.$router.push({ name: 'cliente-login' })
          return
        }
        this.errorGeneral = res.message
      }
      this.generando = false
    },
    async descargarPDF() {
      if (!this.facturaGenerada) return
      this.descargando.pdf = true
      this.errorDescarga = ''
      try {
        const res = await facturacionCliente.descargarPDF(this.facturaGenerada.id)
        const blob = new Blob([res.data], { type: 'application/pdf' })
        this.triggerDownload(blob, `factura_${this.facturaGenerada.folio}.pdf`)
      } catch (e) {
        this.errorDescarga = 'Error al descargar PDF. Intenta nuevamente.'
      } finally {
        this.descargando.pdf = false
      }
    },
    async descargarXML() {
      if (!this.facturaGenerada) return
      this.descargando.xml = true
      this.errorDescarga = ''
      try {
        const res = await facturacionCliente.descargarXML(this.facturaGenerada.id)
        const blob = new Blob([res.data], { type: 'application/xml' })
        this.triggerDownload(blob, `factura_${this.facturaGenerada.folio}.xml`)
      } catch (e) {
        this.errorDescarga = 'Error al descargar XML. Intenta nuevamente.'
      } finally {
        this.descargando.xml = false
      }
    },
    triggerDownload(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    formatFecha(f) {
      if (!f) return ''
      return new Date(f).toLocaleDateString('es-MX')
    },
    formatMonto(m) {
      if (!m) return '0.00'
      return parseFloat(m).toLocaleString('es-MX', { minimumFractionDigits: 2 })
    }
  }
}
</script>
